import React from 'react';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Clock, ExternalLink, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const NewsSection = ({ news, isLoading }) => {
  // Format time ago - using useCallback to memoize
  const timeAgo = React.useCallback((dateString) => {
    if (!dateString) return 'Just now';
    const diff = (Date.now() - new Date(dateString).getTime()) / 1000;
    if (diff < 3600) return 'Just now';
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    return 'Today';
  }, []);

  // Get sentiment icon
  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'bullish':
        return <TrendingUp className="w-4 h-4 text-success" />;
      case 'bearish':
        return <TrendingDown className="w-4 h-4 text-destructive" />;
      default:
        return <Minus className="w-4 h-4 text-muted-foreground" />;
    }
  };

  // Get sentiment badge
  const getSentimentBadge = (sentiment) => {
    switch (sentiment) {
      case 'bullish':
        return <Badge className="badge-success">Bullish</Badge>;
      case 'bearish':
        return <Badge className="badge-warning">Bearish</Badge>;
      default:
        return <Badge className="badge-primary">Neutral</Badge>;
    }
  };

  if (isLoading) {
    return (
      <section id="news" className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <p className="section-subtitle">Latest Updates</p>
            <h2 className="section-title">Market News</h2>
            <div className="w-16 h-1 bg-primary mx-auto mt-4"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i} className="news-card">
                <CardHeader>
                  <Skeleton className="h-6 w-3/4 mb-2" />
                  <Skeleton className="h-4 w-full" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-5/6 mb-2" />
                  <Skeleton className="h-4 w-4/6" />
                </CardContent>
                <CardFooter>
                  <Skeleton className="h-4 w-32" />
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="news" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="section-subtitle">Latest Updates</p>
          <h2 className="section-title text-foreground">Market News</h2>
          <div className="w-16 h-1 bg-primary mx-auto mt-4"></div>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Stay informed with AI-assisted summaries of the latest trading and stock market news from trusted sources.
          </p>
        </div>

        {/* News Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {news.map((item, index) => (
            <Card 
              key={item.id || index} 
              className="news-card group"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <a 
                      href={item.link || '#'} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-lg font-semibold text-primary hover:underline transition-colors group-hover:text-primary-dark"
                    >
                      {item.title}
                      <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </div>
                  {getSentimentIcon(item.sentiment)}
                </div>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                  {item.subtitle}
                </p>
              </CardHeader>

              <CardContent className="pb-3">
                {item.bullets && item.bullets.length > 0 && (
                  <ul className="space-y-2">
                    {item.bullets.map((bullet, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-foreground">
                        <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0"></span>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>

              <CardFooter className="pt-3 border-t border-border flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    {timeAgo(item.date)}
                  </span>
                  <span>•</span>
                  <span className="font-medium">Source: {item.source}</span>
                </div>
                {getSentimentBadge(item.sentiment)}
              </CardFooter>
            </Card>
          ))}
        </div>

        {news.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No news available at the moment. Please refresh.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewsSection;
